/**
 * Blockchain State Management
 *
 * Reactive state for blockchain visualization metrics and UI.
 * Uses Vue refs for reactivity.
 */

import { ref, type Ref } from 'vue';

export class BlockchainState {
  // Block information
  public currentBlock: Ref<number> = ref(1000000); // Start with realistic block number

  // Performance metrics
  public tps: Ref<number> = ref(0);
  public fps: Ref<number> = ref(60);
  public blocksPerSec: Ref<number> = ref(0);       // On-chain block rate (from blockchain timestamps)
  public frontendBps: Ref<number> = ref(0);        // Frontend ingress rate (from Date.now() - shows lag)
  public backendBpsValue: Ref<number> = ref(0);    // Backend's BPS calculation (1-min rolling average)
  public upstreamBps: Ref<number> = ref(0);        // Upstream BPS - raw arrival rate from MegaETH WebSocket
  public gasPerSec: Ref<number> = ref(0);          // Global gas per second (from backend)
  public miniblocksPerSec: Ref<number> = ref(0);   // Global miniblocks per second (from backend)

  // Connection state
  public wsConnected: Ref<boolean> = ref(false);
  public connectionState: Ref<'connected' | 'disconnected' | 'reconnecting'> = ref('disconnected');
  public reconnectAttempt: Ref<number> = ref(0);
  public lastError: Ref<string | null> = ref(null);

  // UI state
  public isPaused: Ref<boolean> = ref(false);
  public chartMode: Ref<'gas' | 'txCount'> = ref('txCount'); // Toggle between gas usage and tx count
  public txMultiplier: Ref<number> = ref(1); // Dev-only: multiply transaction pixels for performance testing (1-1000)
  public isSyncing: Ref<boolean> = ref(false); // True only when saved circles need sync or missing blocks detected

  // Internal tracking for TPS calculation
  private lastTpsUpdate = 0;
  private txCountSinceLastUpdate = 0;

  // Internal tracking for FPS calculation
  private lastFpsUpdate = 0;
  private frameCount = 0;


  // Block timing - circular buffer for O(1) operations (on-chain timestamps)
  private blockTimes: number[] = new Array(10).fill(0);
  private blockTimesIndex: number = 0;
  private blockTimesCount: number = 0;

  // Frontend block timing - circular buffer (Date.now() based, shows network/processing lag)
  private frontendBlockTimes: number[] = new Array(10).fill(0);
  private frontendBlockTimesIndex: number = 0;
  private frontendBlockTimesCount: number = 0;

  // Real-time frontend BPS tracking
  private lastBlockArrivalTime: number = 0;  // When last block started arriving
  private lastFrontendBpsAvg: number = 1.0;  // Rolling average BPS (for smooth interpolation)

  // Backend BPS for blending (received from initial_state)
  private backendBps: number = 0;

  // Sparkline history buffers (last 60 data points, sampled on value change)
  private readonly HISTORY_LENGTH = 60;
  private tpsHistory: number[] = [];
  private gpsHistory: number[] = [];
  private mbpsHistory: number[] = [];
  private bpsHistory: number[] = [];
  private fbpsHistory: number[] = [];
  private backendBpsHistory: number[] = [];
  private upstreamBpsHistory: number[] = [];
  private fpsHistory: number[] = [];

  /**
   * Update TPS metric
   */
  public updateTPS(txCount: number, currentTime: number): void {
    this.txCountSinceLastUpdate += txCount;

    const elapsed = currentTime - this.lastTpsUpdate;
    if (elapsed >= 1000) { // Update every second
      this.tps.value = (this.txCountSinceLastUpdate / elapsed) * 1000;
      this.txCountSinceLastUpdate = 0;
      this.lastTpsUpdate = currentTime;
    }
  }

  /**
   * Update FPS metric
   */
  public updateFPS(currentTime: number): void {
    this.frameCount++;

    const elapsed = currentTime - this.lastFpsUpdate;
    if (elapsed >= 1000) { // Update every second
      this.fps.value = (this.frameCount / elapsed) * 1000;
      this.frameCount = 0;
      this.lastFpsUpdate = currentTime;
    }
  }


  /**
   * Set backend BPS for blending (called when receiving initial_state)
   * Also resets the block times buffer to start fresh
   */
  public setBackendBps(bps: number): void {
    this.backendBps = bps;
    this.backendBpsValue.value = bps; // Store raw backend value for display
    this.blocksPerSec.value = bps;
    // Reset block times buffer - start fresh
    this.blockTimes.fill(0);
    this.blockTimesIndex = 0;
    this.blockTimesCount = 0;
  }

  /**
   * Update on-chain blocks per second metric using circular buffer (O(1))
   * Uses blockchain timestamps - shows actual chain block rate
   */
  public addBlockTime(timeMs: number): void {
    // Circular buffer - overwrite oldest entry
    this.blockTimes[this.blockTimesIndex] = timeMs;
    this.blockTimesIndex = (this.blockTimesIndex + 1) % 10;

    // Track actual count until buffer fills
    if (this.blockTimesCount < 10) {
      this.blockTimesCount++;
    }

    // Calculate average from valid entries (O(n) but n=10 is constant)
    let sum = 0;
    for (let i = 0; i < this.blockTimesCount; i++) {
      sum += this.blockTimes[i];
    }
    const avgBlockTime = sum / this.blockTimesCount;
    const localBps = 1000 / avgBlockTime;

    if (this.blockTimesCount < 10 && this.backendBps > 0) {
      // Blend with backend until buffer is full (smooth transition over ~10 blocks)
      const localWeight = this.blockTimesCount / 10;
      const backendWeight = 1 - localWeight;
      this.blocksPerSec.value = (this.backendBps * backendWeight) + (localBps * localWeight);
    } else {
      // Full buffer - use pure local calculation
      this.blocksPerSec.value = localBps;
    }
  }

  /**
   * Update frontend ingress BPS metric using circular buffer (O(1))
   * Uses Date.now() - shows how fast blocks arrive at frontend (includes network + processing lag)
   * Called when a new block number is seen
   */
  public addFrontendBlockTime(timeMs: number): void {
    // Circular buffer - overwrite oldest entry
    this.frontendBlockTimes[this.frontendBlockTimesIndex] = timeMs;
    this.frontendBlockTimesIndex = (this.frontendBlockTimesIndex + 1) % 10;

    // Track actual count until buffer fills
    if (this.frontendBlockTimesCount < 10) {
      this.frontendBlockTimesCount++;
    }

    // Calculate average from valid entries
    let sum = 0;
    for (let i = 0; i < this.frontendBlockTimesCount; i++) {
      sum += this.frontendBlockTimes[i];
    }
    const avgBlockTime = sum / this.frontendBlockTimesCount;
    this.lastFrontendBpsAvg = 1000 / avgBlockTime;
    this.frontendBps.value = this.lastFrontendBpsAvg;

    // Record when this block arrived (for real-time interpolation)
    this.lastBlockArrivalTime = Date.now();
  }

  /**
   * Update frontend BPS in real-time (call on every miniblock or frame)
   * Interpolates between block arrivals to show smooth falling-behind detection
   */
  public updateFrontendBpsRealtime(): void {
    if (this.lastBlockArrivalTime === 0 || this.lastFrontendBpsAvg === 0) {
      return; // Not enough data yet
    }

    const now = Date.now();
    const timeSinceLastBlock = now - this.lastBlockArrivalTime;

    // Expected time between blocks based on rolling average
    const expectedBlockTime = 1000 / this.lastFrontendBpsAvg;

    // If we're past the expected time, BPS is dropping
    // BPS = 1 / (timeSinceLastBlock / 1000) but clamped to rolling average
    if (timeSinceLastBlock > expectedBlockTime) {
      // We're falling behind - show real-time drop
      // BPS = 1000 / timeSinceLastBlock (how long since last block)
      const realtimeBps = 1000 / timeSinceLastBlock;
      // Blend: weight towards realtime as we fall further behind
      const behindRatio = Math.min(2, timeSinceLastBlock / expectedBlockTime); // Cap at 2x behind
      const realtimeWeight = Math.min(0.8, (behindRatio - 1)); // 0 at 1x, 0.8 at 2x
      this.frontendBps.value = this.lastFrontendBpsAvg * (1 - realtimeWeight) + realtimeBps * realtimeWeight;
    }
    // If we're within expected time, keep the rolling average (don't spike up)
  }

  /**
   * Increment current block number
   */
  public incrementBlock(): void {
    this.currentBlock.value++;
  }

  /**
   * Set current block number
   */
  public setCurrentBlock(blockNumber: number): void {
    this.currentBlock.value = blockNumber;
  }

  /**
   * Reset all metrics
   */
  public reset(): void {
    this.tps.value = 0;
    this.fps.value = 60;
    this.gasPerSec.value = 0;
    this.miniblocksPerSec.value = 0;
    this.txCountSinceLastUpdate = 0;
    this.frameCount = 0;
    this.blockTimes.fill(0);
    this.blockTimesIndex = 0;
    this.blockTimesCount = 0;
  }

  /**
   * Initialize timers (call this on mount)
   */
  public initializeTimers(): void {
    const now = performance.now();
    this.lastTpsUpdate = now;
    this.lastFpsUpdate = now;
  }

  /**
   * Toggle chart mode between gas usage and transaction count
   */
  public toggleChartMode(): void {
    this.chartMode.value = this.chartMode.value === 'gas' ? 'txCount' : 'gas';
  }

  /**
   * Push value to history buffer if it changed
   */
  private pushHistory(buffer: number[], value: number): void {
    // Only add if value changed from last entry
    if (buffer.length === 0 || buffer[buffer.length - 1] !== value) {
      buffer.push(value);
      if (buffer.length > this.HISTORY_LENGTH) {
        buffer.shift();
      }
    }
  }

  /**
   * Update sparkline history (call after metrics update)
   */
  public updateHistory(): void {
    this.pushHistory(this.tpsHistory, this.tps.value);
    this.pushHistory(this.gpsHistory, this.gasPerSec.value);
    this.pushHistory(this.mbpsHistory, this.miniblocksPerSec.value);
    this.pushHistory(this.bpsHistory, this.blocksPerSec.value);
    this.pushHistory(this.fbpsHistory, this.frontendBps.value);
    this.pushHistory(this.backendBpsHistory, this.backendBpsValue.value);
    this.pushHistory(this.upstreamBpsHistory, this.upstreamBps.value);
    this.pushHistory(this.fpsHistory, this.fps.value);
  }

  /**
   * Get history for a specific stat type
   */
  public getHistory(statKey: string): number[] {
    switch (statKey) {
      case 'tps': return this.tpsHistory;
      case 'gps': return this.gpsHistory;
      case 'mbps': return this.mbpsHistory;
      case 'bps': return this.bpsHistory;
      case 'fbps': return this.fbpsHistory;
      case 'bebps': return this.backendBpsHistory;
      case 'ubps': return this.upstreamBpsHistory;
      case 'fps': return this.fpsHistory;
      default: return [];
    }
  }

  /**
   * Clear all history buffers
   */
  public clearHistory(): void {
    this.tpsHistory = [];
    this.gpsHistory = [];
    this.mbpsHistory = [];
    this.bpsHistory = [];
    this.fbpsHistory = [];
    this.backendBpsHistory = [];
    this.upstreamBpsHistory = [];
    this.fpsHistory = [];
  }
}
